// packages/app/src/components/catalog/CatalogValidationPage.tsx
import { useState, useEffect } from 'react';
//import Alert from '@mui/material';
import Alert from '@mui/material/Alert';

import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  CircularProgress,
  Box,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
} from '@material-ui/core';
import { Search, Error, Warning, Info, Refresh } from '@material-ui/icons';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Header,
  Page,
  Content,
  HeaderLabel,
} from '@backstage/core-components';

interface ValidationIssue {
  entityRef: string;
  entityName: string;
  entityKind: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  field?: string;
  relatedEntity?: string;
}

export const CatalogValidationPage = () => {
  const catalogApi = useApi(catalogApiRef);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [debugMode, setDebugMode] = useState(false);

  const validateCatalog = async () => {
    setLoading(true);
    setError(null);
    const allIssues: ValidationIssue[] = [];

    try {
      // Batch fetch all entities at once instead of individual requests
      console.log('Fetching all entities in batch...');
      const startTime = Date.now();

      const [allEntities, allUsers, allGroups, allSystems] = await Promise.all([
        catalogApi.getEntities(), // All entities for relations checking
        catalogApi.getEntities({ filter: { kind: ['User'] } }),
        catalogApi.getEntities({ filter: { kind: ['Group'] } }),
        catalogApi.getEntities({ filter: { kind: ['System'] } }),
      ]);

      const fetchTime = Date.now() - startTime;
      console.log(`Fetched ${allEntities.items.length} entities in ${fetchTime}ms`);

      // Create lookup maps for fast searches
      const userMap = new Map(allUsers.items.map(u => [u.metadata.name.toLowerCase(), u]));
      const groupMap = new Map(allGroups.items.map(g => [g.metadata.name.toLowerCase(), g]));
      const systemMap = new Map(allSystems.items.map(s => [s.metadata.name.toLowerCase(), s]));
      const entityMap = new Map(allEntities.items.map(e => [
        `${e.kind.toLowerCase()}:${e.metadata.namespace || 'default'}/${e.metadata.name}`, e
      ]));

      // Also create name-only maps for systems (multiple systems can have same name in different namespaces)
      const systemsByName = new Map<string, typeof allSystems.items>();
      allSystems.items.forEach(sys => {
        const name = sys.metadata.name.toLowerCase();
        if (!systemsByName.has(name)) {
          systemsByName.set(name, []);
        }
        systemsByName.get(name)!.push(sys);
      });

      console.log(`Created lookup maps: ${userMap.size} users, ${groupMap.size} groups, ${systemMap.size} systems`);

      const validationStartTime = Date.now();

      for (const entity of allEntities.items) {
        const entityRef = `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;

        // Check owner relations (fast lookup using maps)
        if (entity.spec?.owner) {
          const ownerRef = (entity.spec.owner as string).toLowerCase();
          let ownerFound = false;

          if (debugMode) {
            console.log(`\n=== Checking owner for ${entity.metadata.name}: ${ownerRef} ===`);
          }

          // Fast lookup in user and group maps
          if (userMap.has(ownerRef) || groupMap.has(ownerRef)) {
            ownerFound = true;
            if (debugMode) console.log(`✓ Owner found in maps`);
          } else {
            // Check for entity reference formats
            if (ownerRef.includes(':')) {
              const parts = ownerRef.split(':');
              if (parts.length === 2) {
                const [kind, namespacedName] = parts;
                const name = namespacedName.includes('/') ? namespacedName.split('/')[1] : namespacedName;

                if ((kind === 'user' && userMap.has(name.toLowerCase())) ||
                    (kind === 'group' && groupMap.has(name.toLowerCase()))) {
                  ownerFound = true;
                  if (debugMode) console.log(`✓ Owner found by entity reference: ${kind}:${name}`);
                }
              }
            }
          }

          if (!ownerFound) {
            allIssues.push({
              entityRef,
              entityName: entity.metadata.name,
              entityKind: entity.kind,
              severity: 'error',
              message: `Owner '${entity.spec.owner}' not found in catalog`,
              field: 'spec.owner',
              relatedEntity: entity.spec.owner as string,
            });

            if (debugMode) {
              console.log(`✗ Owner '${entity.spec.owner}' not found`);
            }
          }
        }

        // Check system relations (fast lookup using maps)
        if (entity.kind === 'Component' && entity.spec?.system) {
          const systemRef = entity.spec.system as string;
          const systemRefLower = systemRef.toLowerCase();
          let systemFound = false;

          // @ts-ignore: Object is possibly 'null'.
          let foundSystem = null;

          if (debugMode) {
            console.log(`\n=== Checking system for ${entity.metadata.name}: ${systemRef} ===`);
          }

          // Strategy 1: Direct name lookup
          const systemsByThisName = systemsByName.get(systemRefLower) || [];
          if (systemsByThisName.length > 0) {
            // If multiple systems with same name, prefer one in same namespace as component
            const componentNamespace = entity.metadata.namespace || 'default';
            // @ts-ignore: Object is possibly 'null'.
            foundSystem = systemsByThisName.find(s => (s.metadata.namespace || 'default') === componentNamespace)
                         || systemsByThisName[0]; // fallback to first match
            systemFound = true;

            if (debugMode && systemFound) {
              // @ts-ignore: Object is possibly 'null'.
              console.log(`✓ System found by name: ${foundSystem.metadata.name} (namespace: ${foundSystem.metadata.namespace || 'default'})`);
            }
          }

          // Strategy 2: Entity reference lookup
          if (!systemFound && systemRef.includes(':')) {
            const parts = systemRef.split(':');
            if (parts.length === 2 && parts[0].toLowerCase() === 'system') {
              const namespacedName = parts[1];
              const [namespace, name] = namespacedName.includes('/')
                ? namespacedName.split('/')
                : ['default', namespacedName];

              const entityKey = `system:${namespace}/${name}`;
              // @ts-ignore: Object is possibly 'null'.
              foundSystem = entityMap.get(entityKey);
              // @ts-ignore: Object is possibly 'null'.
              systemFound = !!foundSystem;

              if (debugMode && systemFound) {
                console.log(`✓ System found by entity reference: ${entityKey}`);
              }
            }
          }

          if (!systemFound) {
            allIssues.push({
              entityRef,
              entityName: entity.metadata.name,
              entityKind: entity.kind,
              severity: 'error',
              message: `System '${systemRef}' not found in catalog`,
              field: 'spec.system',
              relatedEntity: systemRef,
            });

            if (debugMode) {
              console.log(`✗ System '${systemRef}' not found`);
              console.log('Available systems:', Array.from(systemsByName.keys()));
            }
          } else {
            // Check relations (using pre-loaded data)
            const componentNamespace = entity.metadata.namespace || 'default';
            // @ts-ignore: Object is possibly 'null'.
            const systemNamespace = foundSystem.metadata.namespace || 'default';

            const componentRef = `component:${componentNamespace}/${entity.metadata.name}`;
            // @ts-ignore: Object is possibly 'null'.
            const systemEntityRef = `system:${systemNamespace}/${foundSystem.metadata.name}`;

            if (debugMode) {
              console.log('Checking relations between:', componentRef, '<->', systemEntityRef);
            }

            // Check component -> system relation
            const componentHasSystemRelation = entity.relations?.some(rel => {
              if (rel.type !== 'partOf') return false;
              const targetRef = rel.targetRef;
              // @ts-ignore: Object is possibly 'null'.
              const systemName = foundSystem.metadata.name;

              return targetRef === systemEntityRef ||
                     targetRef === `system:${systemName}` ||
                     targetRef === systemName ||
                     targetRef === `${systemNamespace}/${systemName}`;
            });

            // Check system -> component relation
            // @ts-ignore: Object is possibly 'null'.
            const systemHasComponentRelation = foundSystem.relations?.some(rel => {
              if (rel.type !== 'hasPart') return false;
              const targetRef = rel.targetRef;
              const componentName = entity.metadata.name;

              return targetRef === componentRef ||
                     targetRef === `component:${componentName}` ||
                     targetRef === componentName ||
                     targetRef === `${componentNamespace}/${componentName}`;
            });

            if (!componentHasSystemRelation) {
              allIssues.push({
                entityRef,
                entityName: entity.metadata.name,
                entityKind: entity.kind,
                severity: 'warning',
                message: `Component is missing 'partOf' relation to system '${systemRef}'`,
                field: 'relations',
                relatedEntity: systemRef,
              });
            }

            if (!systemHasComponentRelation) {
              allIssues.push({
                entityRef,
                entityName: entity.metadata.name,
                entityKind: entity.kind,
                severity: 'warning',
                message: `System '${systemRef}' is missing 'hasPart' relation to component '${entity.metadata.name}'`,
                field: 'relations',
                relatedEntity: systemRef,
              });
            }
          }
        }

        // Check general relations (using pre-loaded entity map)
        if (entity.relations && entity.relations.length > 0) {
          for (const relation of entity.relations) {
            const targetRef = relation.targetRef;
            let relationFound = false;

            // Try to find the target entity in our pre-loaded data
            // Check various possible formats
            const possibleRefs = [
              targetRef,
              targetRef.includes(':') ? targetRef : `component:default/${targetRef}`,
              targetRef.includes(':') ? targetRef : `system:default/${targetRef}`,
              targetRef.includes(':') ? targetRef : `user:default/${targetRef}`,
              targetRef.includes(':') ? targetRef : `group:default/${targetRef}`,
            ];

            relationFound = possibleRefs.some(ref => entityMap.has(ref.toLowerCase()));

            if (!relationFound) {
              // Try name-only lookups
              const targetName = targetRef.split('/').pop() || targetRef.split(':').pop() || targetRef;
              relationFound = userMap.has(targetName.toLowerCase()) ||
                            groupMap.has(targetName.toLowerCase()) ||
                            systemsByName.has(targetName.toLowerCase()) ||
                            Array.from(entityMap.values()).some(e => e.metadata.name.toLowerCase() === targetName.toLowerCase());
            }

            if (!relationFound) {
              allIssues.push({
                entityRef,
                entityName: entity.metadata.name,
                entityKind: entity.kind,
                severity: 'warning',
                message: `Related entity '${targetRef}' (${relation.type}) not found`,
                field: 'relations',
                relatedEntity: targetRef,
              });
            }
          }
        }
      }

      const validationTime = Date.now() - validationStartTime;
      console.log(`Validation completed in ${validationTime}ms. Found ${allIssues.length} issues.`);
      console.log(`Total time: ${Date.now() - startTime}ms`);

      } catch (error) {
      console.error('Failed to validate catalog:', error);
      setError(`Failed to validate catalog: ${error}`);
    }

    setIssues(allIssues);
    setLoading(false);
  };

  useEffect(() => {
    validateCatalog();
  }, [catalogApi]);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <Error style={{ color: '#f44336' }} />;
      case 'warning':
        return <Warning style={{ color: '#ff9800' }} />;
      default:
        return <Info style={{ color: '#2196f3' }} />;
    }
  };

  const filteredIssues = issues.filter(issue => {
    const matchesSearch = searchTerm === '' ||
      issue.entityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.relatedEntity && issue.relatedEntity.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesTab = currentTab === 0 ||
      (currentTab === 1 && issue.severity === 'error') ||
      (currentTab === 2 && issue.severity === 'warning') ||
      (currentTab === 3 && issue.severity === 'info');

    return matchesSearch && matchesTab;
  });

  return (
    <Page themeId="tool">
      <Header
        title="Catalog Validation Issues"
        subtitle="View and resolve entity relation and validation problems"
      >
        <HeaderLabel label="Owner" value="Platform Team" />
        <HeaderLabel label="Lifecycle" value="Production" />
      </Header>
      <Content>
        {error && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <TextField
              placeholder="Search entities or messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              style={{ width: 300 }}
            />

            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={validateCatalog}
              disabled={loading}
            >
              Refresh
            </Button>

            <Button
              variant="outlined"
              onClick={() => setDebugMode(!debugMode)}
              size="small"
              color={debugMode ? "secondary" : "default"}
            >
              {debugMode ? 'Hide Debug' : 'Show Debug'}
            </Button>
          </Box>

          <Typography variant="body2" color="textSecondary">
            {loading ? 'Validating...' : `Found ${filteredIssues.length} issues across ${new Set(issues.map(i => i.entityRef)).size} entities`}
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
            <Typography variant="h6" style={{ marginLeft: 16 }}>
              Validating catalog...
            </Typography>
          </Box>
        ) : (
          <>
            <Tabs value={currentTab} onChange={(_, value) => setCurrentTab(value)} style={{ marginBottom: 16 }}>
              <Tab label={`All (${issues.length})`} />
              <Tab label={`Errors (${issues.filter(i => i.severity === 'error').length})`} />
              <Tab label={`Warnings (${issues.filter(i => i.severity === 'warning').length})`} />
              <Tab label={`Info (${issues.filter(i => i.severity === 'info').length})`} />
            </Tabs>

            {filteredIssues.length === 0 ? (
              <Alert severity="success">
                {issues.length === 0
                  ? "No validation issues found! All entity relations are valid."
                  : "No issues match your current filter criteria."}
              </Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Severity</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell>Kind</TableCell>
                      <TableCell>Issue</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Related Entity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredIssues.map((issue, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip
                            icon={getSeverityIcon(issue.severity)}
                            label={issue.severity}
                            size="small"
                            color={issue.severity === 'error' ? 'secondary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            onClick={() => {
                              const [kind, namespace, name] = issue.entityRef.split(/[:/]/);
                              window.open(`/catalog/${namespace || 'default'}/${kind.toLowerCase()}/${name}`, '_blank');
                            }}
                          >
                            {issue.entityName}
                          </Link>
                        </TableCell>
                        <TableCell>{issue.entityKind}</TableCell>
                        <TableCell>{issue.message}</TableCell>
                        <TableCell>{issue.field || '-'}</TableCell>
                        <TableCell>{issue.relatedEntity || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Content>
    </Page>
  );
};
